import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { solutions } from './solutionsData';
import './SolutionDetail.css';

export const SolutionDetail = () => {
  const { slug } = useParams();
  const reduced = useReducedMotion();
  const data = solutions[slug];

  if (!data) return <Navigate to="/solutions" replace />;

  const HeroIcon = data.icon;

  const fade = {
    hidden: { opacity: 0, y: reduced ? 0 : 20 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: reduced ? 0.2 : 0.6, delay: reduced ? 0 : i * 0.07, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <div className="sd-page">
      {/* Hero */}
      <section className="sd-hero">
        <div className="sd-hero-glow" aria-hidden="true" />
        <motion.div className="sd-hero-icon" initial="hidden" animate="visible" variants={fade}>
          <HeroIcon size={28} />
        </motion.div>
        <motion.span className="sd-eyebrow" initial="hidden" animate="visible" variants={fade} custom={1}>
          {data.eyebrow}
        </motion.span>
        <motion.h1 className="sd-title" initial="hidden" animate="visible" variants={fade} custom={2}>
          {data.title}
        </motion.h1>
        <motion.p className="sd-subtitle" initial="hidden" animate="visible" variants={fade} custom={3}>
          {data.subtitle}
        </motion.p>
        <motion.div className="sd-hero-cta" initial="hidden" animate="visible" variants={fade} custom={4}>
          <Link to="/contact" className="btn-primary">
            Talk to us <ArrowRight size={18} />
          </Link>
          <Link to="/solutions" className="btn-secondary">All solutions</Link>
        </motion.div>
      </section>

      {/* What's included */}
      <section className="sd-included">
        <h2 className="sd-section-title">Everything you need for a production agent</h2>
        <div className="sd-included-grid">
          {data.included.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                className="sd-feature"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                variants={fade}
                custom={i % 3}
              >
                <div className="sd-feature-icon"><Icon size={20} /></div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Use cases + integrations */}
      <section className="sd-meta">
        <div className="sd-meta-grid">
          <div className="sd-usecases">
            <h2 className="sd-meta-title">Use cases</h2>
            <ul>
              {data.useCases.map((u) => (
                <li key={u}><Check size={16} /> {u}</li>
              ))}
            </ul>
          </div>
          <div className="sd-integrations">
            <h2 className="sd-meta-title">Works with</h2>
            <div className="sd-int-chips">
              {data.integrations.map((i) => (
                <span key={i} className="sd-int-chip">{i}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sd-cta">
        <div className="sd-cta-glow" aria-hidden="true" />
        <h2>{data.ctaTitle}</h2>
        <p>We start every project with a free scoping call. No commitment. Just clarity.</p>
        <Link to="/contact" className="btn-primary">
          Book a Scoping Call <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
};
